import { getEnabledMenuItems } from '../constants/featureMenuMapping'

/**
 * Filter menu items based on enabled features
 * 
 * Key behavior:
 * - If features are not loaded (empty object): return EMPTY array (don't show menu items before ready)
 * - If features are loaded: return only items where feature is enabled
 * 
 * @param {Array} menuItems - Array of menu item objects
 * @param {Object} features - Feature flags object from context
 * @param {string} userRole - User role (schoolAdmin, teacher, student, staff)
 * @returns {Array} Filtered array of menu items that should be shown
 */
export function filterMenuByFeatures(menuItems, features, userRole) {
  if (!menuItems || !Array.isArray(menuItems)) {
    return []
  }

  if (!features || Object.keys(features).length === 0) {
    console.log('[FILTER_MENU] No features loaded, returning EMPTY array (menu items hidden until features ready)')
    return []
  }

  console.log(`[FILTER_MENU] Features loaded (${Object.keys(features).length} features), filtering menu for role: ${userRole}`);

  // Get set of enabled menu item IDs for this role
  const enabledMenuIds = getEnabledMenuItems(userRole, features)
  console.log(`[FILTER_MENU] Enabled menu IDs for ${userRole}:`, Array.from(enabledMenuIds));

  // Filter menu items
  const filtered = menuItems.filter(item => {
    // If item has no ID, keep it (might be a divider or heading)
    if (!item.id) {
      return true
    }

    // Check if this menu item ID is in the enabled set
    const isEnabled = enabledMenuIds.has(item.id)
    if (isEnabled) {
      console.log(`[FILTER_MENU]   ✓ ${item.id} (${item.label}): ENABLED`);
    }
    return isEnabled
  })

  console.log(`[FILTER_MENU] Filtered menu: ${filtered.length}/${menuItems.length} items visible`);
  return filtered
}

/**
 * Check if a menu item should be visible based on features
 * 
 * @param {string} menuItemId - Menu item ID to check
 * @param {Object} features - Feature flags object
 * @param {string} userRole - User role
 * @returns {boolean} True if menu item should be shown
 */
export function isMenuItemVisible(menuItemId, features, userRole) {
  // If no features configured, return false (don't allow items to show before ready)
  if (!features || Object.keys(features).length === 0) {
    console.log(`[FILTER_MENU] isMenuItemVisible("${menuItemId}"): features not loaded, returning FALSE`);
    return false
  }

  const enabledMenuIds = getEnabledMenuItems(userRole, features)
  const isVisible = enabledMenuIds.has(menuItemId)
  console.log(`[FILTER_MENU] isMenuItemVisible("${menuItemId}"): ${isVisible}`);
  return isVisible
}
